/**
 * Staff session of the Operator_Console over oidc-client-ts (authorization code + PKCE
 * against Keycloak, client `operator-console`, Req 3.1). Token storage and silent restore
 * are shared with the Admin_Portal (@macksons/web-client `oidc.ts`): tokens stay in memory.
 *
 * Observable for `useSyncExternalStore`:
 *  - `loading` until the first restore attempt finishes;
 *  - `signedIn` with the access token's staff claims (read like the API's StaffJwtGuard);
 *  - `signedOut` otherwise.
 *
 * Offline behaviour: an access token that expires while the device is offline does NOT
 * sign the operator out; the next API call renews it (refresh token in memory) once the
 * network is back. Only an explicit sign-out or an API 401 (`expire()`) ends the session.
 */
import type { User, UserManager } from 'oidc-client-ts';
import { safeReturnTo, staffClaimsFromAccessToken, type SigninState, type StaffClaims } from '@macksons/web-client';

export type SessionState =
  | { status: 'loading' }
  | { status: 'signedOut' }
  | { status: 'signedIn'; claims: StaffClaims };

export interface Session {
  get(): SessionState;
  subscribe(listener: () => void): () => void;
  /** Re-reads the current user (silent restore when none is in memory). */
  refresh(): Promise<SessionState>;
  /** Completes the redirect callback once (StrictMode-safe) and returns the return path. */
  completeSignin(): Promise<string>;
  signIn(returnTo?: string): Promise<void>;
  signOut(): Promise<void>;
  /** The API rejected the token (401): drop local tokens, keep the Keycloak SSO session. */
  expire(): Promise<void>;
}

export interface SessionDeps {
  userManager: Pick<UserManager, 'signinRedirect' | 'signoutRedirect' | 'removeUser' | 'signinRedirectCallback'>;
  /** Current valid user (restoring silently when needed), or null. */
  currentUser: () => Promise<User | null>;
}

function stateOf(user: User | null): SessionState {
  const claims = user ? staffClaimsFromAccessToken(user.access_token) : undefined;
  return claims ? { status: 'signedIn', claims } : { status: 'signedOut' };
}

export function createSession({ userManager, currentUser }: SessionDeps): Session {
  let state: SessionState = { status: 'loading' };
  const listeners = new Set<() => void>();
  const set = (next: SessionState) => {
    state = next;
    for (const l of listeners) l();
  };
  let signin: Promise<string> | undefined;

  return {
    get: () => state,
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    async refresh() {
      set(stateOf(await currentUser()));
      return state;
    },
    completeSignin() {
      signin ??= userManager.signinRedirectCallback().then((user) => {
        set(stateOf(user));
        return safeReturnTo((user.state as SigninState | undefined)?.returnTo);
      });
      return signin;
    },
    async signIn(returnTo) {
      const signinState: SigninState = { returnTo: safeReturnTo(returnTo) };
      await userManager.signinRedirect({ state: signinState });
    },
    async signOut() {
      try {
        await userManager.signoutRedirect();
      } catch {
        // Keycloak unreachable (e.g. offline): drop the local session at least.
        await userManager.removeUser();
        set({ status: 'signedOut' });
      }
    },
    async expire() {
      await userManager.removeUser();
      set({ status: 'signedOut' });
    },
  };
}
